import escapeRegExp from 'lodash/escapeRegExp';

/**
 * Shared client/server protocol for the guided PR tour.
 *
 * The tour has no client-side state: the chat system prompt instructs the
 * model to end every in-tour response with the footer below, and the client
 * derives the "Next stop / Previous stop / End tour" quick replies by parsing
 * that footer off the last assistant message. Keeping the strings here means
 * the prompt and the parser can never drift.
 */

/** Minimum review-plan size before the empty-chat "Tour this PR" chip shows. */
export const TOUR_MIN_ORDERED_FILES = 4;

/** Sent verbatim as the user message when the tour chip is clicked (<=130 chars). */
export const TOUR_TRIGGER_ACTION =
  'Start a guided tour of this PR: take me through the changes stop by stop in review-plan order, starting at stop 1.';

// Sent verbatim as the user message by the tour controls. The prompt's
// tour-control rules interpolate them, so the buttons and the model agree.
export const TOUR_NEXT_TEXT = 'next';
export const TOUR_BACK_TEXT = 'back';
export const TOUR_END_TEXT = 'end tour';

/** Embedded in the chat system prompt; {n}/{m} are filled in by the model. */
export const TOUR_FOOTER_TEMPLATE = `Stop {n} of {m} — say "${TOUR_NEXT_TEXT}" to continue or "${TOUR_END_TEXT}" to finish.`;

// Footer lives on the last line of the message; only search near the tail so a
// prose mention of "stop 3 of 12" earlier in a long answer can't false-match.
const TOUR_FOOTER_TAIL_WINDOW = 220;

// Tolerant of dash style ("—", "-", "--"), quote/markdown emphasis around
// "next" (*next*, `next`, "next"), and casing — selectable chat models
// (DeepSeek/Qwen) are sloppy about reproducing exact punctuation.
const TOUR_FOOTER_PATTERN = new RegExp(
  `stop\\s+(\\d+)\\s+of\\s+(\\d+)\\s*(?:[—–-]{1,2}|[.:])\\s*say\\s+[*_\`"'‘’“”]*${escapeRegExp(TOUR_NEXT_TEXT)}`,
  'i',
);

export type TourFooter = {
  stop: number;
  total: number;
};

export function parseTourFooter(text: string): TourFooter | null {
  const tail = text.trimEnd().slice(-TOUR_FOOTER_TAIL_WINDOW);
  const match = tail.match(TOUR_FOOTER_PATTERN);
  if (!match) {
    return null;
  }

  const stop = Number(match[1]);
  const total = Number(match[2]);
  if (
    !Number.isInteger(stop) ||
    !Number.isInteger(total) ||
    stop < 1 ||
    total < 1 ||
    stop > total
  ) {
    return null;
  }

  return { stop, total };
}
